/**
 * Studio Context
 *
 * Provides studio mode, limits, and repository to the app.
 * Thin provider - keeps business logic in services.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'
import { useFamily } from './FamilyContext'
import {
  getStudioMode,
  getLimits,
  isReadOnly,
  shouldUseCloudRepository,
} from '@/services/entitlementsService'
import { getStoryRepository, type IStoryRepository } from '@/repositories'
import {
  migrateLocalStudioToCloud,
  hasPendingMigration,
} from '@/services/migrationService'
import type { StudioMode, StudioLimits } from '@/types/studio'

interface StudioContextValue {
  /** Current studio mode: 'local' or 'cloud' */
  mode: StudioMode
  /** Feature limits based on mode and subscription */
  limits: StudioLimits
  /** Story repository (local or cloud based on mode) */
  repository: IStoryRepository
  /** Whether writes are disabled (lapsed subscription) */
  readOnly: boolean
  /** Whether there's local data waiting to be migrated */
  hasPendingMigration: boolean
  /** Whether migration is currently in progress */
  isMigrating: boolean
  /** Trigger migration manually (returns success/failure) */
  triggerMigration: () => Promise<boolean>
  /** Whether the context is still loading */
  isLoading: boolean
}

const StudioContext = createContext<StudioContextValue | undefined>(undefined)

export function StudioProvider({ children }: { children: ReactNode }) {
  const { family, activeWriter, isLoading: familyLoading } = useFamily()
  const [isMigrating, setIsMigrating] = useState(false)
  const [migrationComplete, setMigrationComplete] = useState(false)
  const previousModeRef = useRef<StudioMode>('local')

  // Compute mode from family subscription
  const mode = useMemo(() => getStudioMode(family), [family])

  // Compute read-only state (premium but lapsed)
  const readOnly = useMemo(() => isReadOnly(family), [family])

  // Compute limits based on mode and readOnly
  const limits = useMemo(() => getLimits(mode, readOnly), [mode, readOnly])

  // Get the appropriate repository
  const repository = useMemo(
    () => getStoryRepository(family, activeWriter?.id ?? null),
    [family, activeWriter?.id]
  )

  // Check for pending migration
  const hasPending = useMemo(() => {
    if (!activeWriter) return false
    if (migrationComplete) return false
    return hasPendingMigration(activeWriter.id)
  }, [activeWriter, migrationComplete])

  // Detect mode transition and trigger migration
  useEffect(() => {
    const previousMode = previousModeRef.current
    previousModeRef.current = mode

    // Only trigger migration on transition from local to cloud
    if (previousMode === 'local' && mode === 'cloud' && hasPending && !isMigrating) {
      triggerMigration()
    }
  }, [mode, hasPending, isMigrating])

  const triggerMigration = useCallback(async (): Promise<boolean> => {
    if (!activeWriter) return false
    if (isMigrating) return false

    // Only migrate if we're in cloud mode (upgraded)
    if (!shouldUseCloudRepository(family)) return false

    setIsMigrating(true)
    try {
      const result = await migrateLocalStudioToCloud(activeWriter.id)
      if (result.success) {
        setMigrationComplete(true)
        return true
      }
      console.error('Migration failed:', result.error)
      return false
    } finally {
      setIsMigrating(false)
    }
  }, [activeWriter, family, isMigrating])

  return (
    <StudioContext.Provider
      value={{
        mode,
        limits,
        repository,
        readOnly,
        hasPendingMigration: hasPending,
        isMigrating,
        triggerMigration,
        isLoading: familyLoading || isMigrating,
      }}
    >
      {children}
    </StudioContext.Provider>
  )
}

export function useStudio() {
  const context = useContext(StudioContext)
  if (context === undefined) {
    throw new Error('useStudio must be used within a StudioProvider')
  }
  return context
}

/**
 * Convenience hook to get just the repository
 */
export function useStoryRepository() {
  const { repository } = useStudio()
  return repository
}

/**
 * Convenience hook to check if user is in local mode
 */
export function useIsLocalMode() {
  const { mode } = useStudio()
  return mode === 'local'
}
